import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { UserUpdateBody } from "../api/models/user.models";
import { useTranslation } from "react-i18next";
import { updateSelf, fetchSelf } from "../api/self.api";
import { useAuth } from "../contexts/authContext";

const useSelf = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [bearerToken, toast] = useUIStore(
    useShallow((state: UIState) => [state.bearerToken, state.toastRef])
  );
  const { isLoggedIn } = useAuth();

  const {
    data: userData,
    error: userError,
    isLoading: isLoadingUser,
  } = useQuery({
    queryKey: ["self"],
    queryFn: () => fetchSelf(bearerToken as string),
    enabled: !!isLoggedIn,
  });

  const updateUserInfo = async (payload: UserUpdateBody) => {
    const result = await updateUserMutation.mutateAsync(payload);
    return result;
  };

  const updateUserMutation = useMutation({
    mutationFn: (payload: UserUpdateBody) =>
      updateSelf(bearerToken as string, { ...payload }),
    onError: (error) => {
      console.error("Failed to update user:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("label-user-update-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["self"] });
      /* toast?.current?.show({
        severity: "success",
        detail: t("label-user-updated"),
        life: 3000,
      }); */
    },
  });

  const isUpdatingUser = updateUserMutation.isPending;

  return {
    updateUserInfo,
    isUpdatingUser,
    userData,
    isLoadingUser,
  };
};

export default useSelf;
